import React, { useEffect, useState, useContext, useRef } from "react";
import { PatientContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";

import { useHistory, useLocation } from "react-router-dom";
import { Row, Col, Form } from "react-bootstrap";

import { getTherapiesList, postPreviousTherapies } from "../../api/TBIRequests";

import useListSelector from "../../lib/useListSelector";
import Tab from "react-bootstrap/Tab";
import Nav from "react-bootstrap/Nav";
import MedicalListSelector from "../UIComponents/MedicalListSelector";
import Sallie from "../UIComponents/Sallie";
import OnboardingStepHeader from "./OnboardingStepHeader";
import SubmitButton from "../UIComponents/SubmitButton";
import customizeMessages from "../../lib/customizeMessages";

const SelectTherapies = ({ setOnboardingPercent, user }) => {
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [returnedData, setReturnedData] = useState(["menstrual cycle", "xvcxzvxzv"]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);

  const availableTherapiesContainerRef = useRef(null);
  const selectedTherapiesContainerRef = useRef(null);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);

  const history = useHistory();
  const location = useLocation();

  const { toggleFromSelectedList, switchSingleSelection, addOther, selectedData, initialize } =
    useListSelector({ setInitialData: setReturnedData, initialData: returnedData });

  const categories = ["Alternative", "Applied", "Chiropractic", "Vision", "Mental Health", "Other"];
  const currentCategory = categories[currentCategoryIndex];

  const colors = {
    Alternative: "#dab7ec", Applied: "#ffdadb", Chiropractic: "#ffdcaf",
    Vision: "#fff3c4", "Mental Health": "#c1fdf7", Other: "#f4f3ee",
  };

  const createItemListFromRes = (res) => {
    return categories.map((category) => {
      const items = res.data.filter((item) => item.category === category).map((item) => item.therapies);
      return { category, items };
    });
  };

  useEffect(() => {
    location.state?.fromPatientControl && user.userType != "patient"
      ? setOnboardingPercent(100)
      : setOnboardingPercent(70);
    getTherapiesList()
      .then((res) => {
        const itemList = createItemListFromRes(res);
        setReturnedData(itemList);
        initialize(itemList);
        setLoading(false);
      })
      .catch(() => history.push("/oops"));
  }, [t]);

  useEffect(() => {
    for (let category in selectedData) {
      if (selectedData[category].includes("None") || selectedData[category].includes("Ninguno")) {
        const selectedArrayWithoutNone = [...selectedData[category]];
        selectedArrayWithoutNone.splice(selectedArrayWithoutNone.indexOf("None"));
        selectedArrayWithoutNone.forEach((item) => {
          toggleFromSelectedList(item, selectedArrayWithoutNone, category);
        });
      }
    }
  }, [selectedData]);

  useEffect(() => {
    if (availableTherapiesContainerRef.current) availableTherapiesContainerRef.current.scrollTop = 0;
  }, [currentCategoryIndex]);

  useEffect(() => {
    if (selectedTherapiesContainerRef.current)
      selectedTherapiesContainerRef.current.scrollTop = selectedTherapiesContainerRef.current.scrollHeight;
  }, [selectedData]);

  const handlePrev = () => {
    if (currentCategoryIndex > 0) setCurrentCategoryIndex((prev) => prev - 1);
  };

  const handleNextOrSubmit = () => {
    if (currentCategoryIndex < categories.length - 1) {
      setCurrentCategoryIndex((prev) => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const processDataForSending = () => {
    return Object.entries(selectedData).flatMap(([category, itemList]) =>
      itemList.map((item) => ({ therapies: item }))
    );
  };

  const handleSubmit = () => {
    setSubmitting(true);
    const processedData = processDataForSending();
    if (processedData.length < 1) {
      setErrorMessage(true);
      setSubmitting(false);
      return;
    }
    if (processedData.length === 0) {
      location.state?.fromPatientControl && user.userType != "patient"
        ? history.push("/patientControl")
        : history.push("/notificationPrefs");
    } else {
      postPreviousTherapies({ patientId: patient.patientId, data: processedData })
        .then(() => {
          location.state?.fromPatientControl && user.userType != "patient"
            ? history.push("/patientControl")
            : history.push("/notificationPrefs");
        })
        .catch(() => history.push("/oops"));
    }
  };

  const getPossibleMessages = () => ({
    mainHeader: {
      Stroke: t("therapies.header"),
      tbiPatient: t("therapies.header"),
      caregiver: t("therapies.header"),
    },
    sallieText: {
      Stroke: t("therapies.sallie"),
      tbiPatient: t("therapies.sallie"),
      caregiver: t("therapies.sallie"),
    },
  });

  const getMessage = customizeMessages({ user, getPossibleMessages });

  const messages = {
    Alternative: t("therapies.category.alternative"),
    Applied: t("therapies.category.applied"),
    Chiropractic: t("therapies.category.chiropractic"),
    Vision: t("therapies.category.vision"),
    "Mental Health": t("therapies.category.mental_health"),
    Other: t("therapies.category.other"),
  };

  const tTherapy = (category, factor) => {
    const normCat = category.toLowerCase().replace(/ /g,"_");
    const safe = factor.trim().toLowerCase()
      .replace(/ /g,"_").replace(/\//g,"_").replace(/,/g,"")
      .replace(/:/g,"").replace(/'/g,"").replace(/[()]/g,"").replace(/-/g,"_")
      .substring(0, 60);
    const key = `therapy.${normCat}.${safe}`;
    const translated = t(key);
    return translated !== key ? translated : factor;
  };

  return (
    <>
      {loading ? (
        t("common.loading")
      ) : (
        <>
          <OnboardingStepHeader text={getMessage("mainHeader")} />
          <Sallie text={getMessage("sallieText")} />
          <Row className="justify-content-between" style={{ marginTop: "1rem" }}>
            <Col sm={8}>
              <h5 style={{ textAlign: "center" }}>{t("therapies.category_header")}</h5>
            </Col>
            <Col sm={4}>
              <h5 style={{ textAlign: "center" }}>{t("therapies.list_header")}</h5>
            </Col>
          </Row>
          <Tab.Container id="left-tabs-example" activeKey={currentCategory}>
            <Row style={{ marginTop: "0.5rem" }}>
              <Col sm={3}>
                <Nav fill justify variant="tabs" className="flex-column">
                  {returnedData.map((itemList) => (
                    <Nav.Item key={itemList.category}>
                      <Nav.Link
                        eventKey={itemList.category}
                        disabled
                        style={{
                          background: `${colors[itemList.category]}`,
                          border: "1px solid #106995",
                          fontWeight: itemList.category === currentCategory ? "bold" : "normal",
                        }}
                      >
                        {messages[itemList.category]}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </Col>
              <Col sm={5}>
                <Tab.Content ref={availableTherapiesContainerRef}>
                  {returnedData.map((itemList, i) => (
                    <Tab.Pane key={itemList.category} eventKey={itemList.category}>
                      <MedicalListSelector
                        patient={patient}
                        category={itemList.category}
                        message={messages[itemList.category]}
                        list={itemList.items}
                        index={i}
                        selectedList={selectedData[itemList.category]}
                        toggleFromSelectedList={toggleFromSelectedList}
                        selectOne={switchSingleSelection}
                        addOther={addOther}
                        noOther={false}
                        translationNamespace="therapy"
                      />
                    </Tab.Pane>
                  ))}
                </Tab.Content>
              </Col>
              <Col sm={4}>
                <div
                  ref={selectedTherapiesContainerRef}
                  className="justify-content-left"
                  style={{
                    height: "30rem", overflow: "scroll", color: "black",
                    border: "1px solid #106995", textAlign: "left", background: "#fff",
                    padding: "2rem", borderRadius: "5px", margin: ".25rem 0",
                  }}
                >
                  {Object.entries(selectedData).flatMap(([category, itemList]) =>
                    itemList.map((item) => (
                      <div key={item} className="flex items-start gap-2 my-2">
                        <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1"
                          xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink"
                          className="flex-shrink-0">
                          <title>icon/action/check_ccircle_24px</title>
                          <defs>
                            <path d="M12,2 C6.48,2 2,6.48 2,12 C2,17.52 6.48,22 12,22 C17.52,22 22,17.52 22,12 C22,6.48 17.52,2 12,2 Z M10,17 L5,12 L6.41,10.59 L10,14.17 L17.59,6.58 L19,8 L10,17 Z" id="path-1"></path>
                          </defs>
                          <g id="icon/action/check_ccircle_24px" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                            <mask id="mask-2" fill="white"><use xlinkHref="#path-1"></use></mask>
                            <use id="-↳Color" fill={colors[category]} xlinkHref="#path-1"></use>
                          </g>
                        </svg>{" "}{tTherapy(category, item)}
                      </div>
                    ))
                  )}
                </div>
              </Col>
            </Row>
          </Tab.Container>

          {errorMessage && (
            <Form.Control.Feedback style={{ display: "block" }} type="invalid">
              {t("therapies.error")}
            </Form.Control.Feedback>
          )}

          <Row className="d-flex justify-content-between align-items-center" style={{ marginTop: "1rem" }}>
            <Col xs="auto">
              <SubmitButton type="button" onClick={handlePrev}
                disabled={currentCategoryIndex === 0} greyout={currentCategoryIndex === 0}>
                {t("selectlatersymptoms.previous_category")}
              </SubmitButton>
            </Col>
            <Col xs="auto">
              <SubmitButton disabled={submitting} onClick={handleNextOrSubmit}>
                {currentCategoryIndex < categories.length - 1
                  ? t("selectlatersymptoms.next_category")
                  : `${t("onboarding.saveContinue")} >`}
              </SubmitButton>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default SelectTherapies;